// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { Post } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { CloseIcon, TrashIcon, PlusIcon } from './Icons';

const MinusIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
    </svg>
);

interface ShoppingCartDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    cart: { post: Post; quantity: number }[];
    onRemoveFromCart: (postId: number) => void;
    onUpdateQuantity: (postId: number, delta: number) => void;
    onClearCart: () => void;
    setFlashMessage: (msg: string | null) => void;
}

export const ShoppingCartDrawer = ({
    isOpen,
    onClose,
    cart,
    onRemoveFromCart,
    onUpdateQuantity,
    onClearCart,
    setFlashMessage
}: ShoppingCartDrawerProps) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData[currentUser || ''];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';

    const [isCheckingOut, setIsCheckingOut] = useState(false);

    // Group items by Vendor (author / vendorId)
    const groupedItems = useMemo(() => {
        const groups: Record<string, typeof cart> = {};
        cart.forEach(item => {
            const vendorId = item.post.vendorId || item.post.author || 'unknown';
            if (!groups[vendorId]) groups[vendorId] = [];
            groups[vendorId].push(item);
        });
        return groups;
    }, [cart]);

    // Calculate subtotal
    const subtotal = useMemo(() => {
        return cart.reduce((acc, item) => acc + (item.post.price || 0) * item.quantity, 0);
    }, [cart]);

    const estTax = subtotal * 0.08;
    const total = subtotal + estTax;

    if (!isOpen) return null;

    const handleCheckout = () => {
        if (cart.length === 0) return;
        setIsCheckingOut(true);

        setTimeout(() => {
            setIsCheckingOut(false);
            onClearCart();
            onClose();

            // Notify about successful order
            const vendorNames = Object.keys(groupedItems).map(id => {
                const vendorData = usersData[id];
                return vendorData?.vendorName || vendorData?.displayName || id;
            });

            setFlashMessage(`🎉 Checkout successful! Ordered from: ${vendorNames.join(', ')}`);
            setTimeout(() => setFlashMessage(null), 4000);
        }, 2000);
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end">
            {/* Backdrop overlay */}
            <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 animate-fade-in"
                onClick={onClose}
            />

            {/* Sliding Drawer container */}
            <div 
                className="relative h-full w-full max-w-md bg-zinc-950/90 border-l border-white/10 shadow-2xl flex flex-col transform transition-transform duration-500 ease-out"
                style={{ color: userTextColor }}
            >
                {/* Header */}
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-2xl">🛒</span>
                        <h2 className="text-xl font-black uppercase tracking-wider" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                            Shopping Cart
                        </h2>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                        style={{ color: userTextColor }}
                    >
                        <CloseIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">
                    {isCheckingOut ? (
                        <div className="h-full flex flex-col items-center justify-center space-y-4 animate-pulse">
                            <div className="relative w-16 h-16">
                                <div className="absolute inset-0 border-4 border-green-500/20 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                            <p className="text-sm font-black uppercase tracking-widest text-center" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                                Processing Secure Payment...
                            </p>
                            <p className="text-xs text-white/60 text-center">Verifying items with storefront merchants</p>
                        </div>
                    ) : cart.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                            <span className="text-6xl animate-bounce">🛍️</span>
                            <h3 className="text-lg font-black uppercase tracking-wider" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                                Cart is Empty
                            </h3>
                            <p className="text-xs text-white/50 max-w-xs">
                                Visit vendor stores and tap "Add to Cart" on listing posts to add items.
                            </p>
                        </div>
                    ) : (
                        Object.entries(groupedItems).map(([vendorId, items]) => {
                            const vendorData = usersData[vendorId];
                            const vendorName = vendorData?.vendorName || vendorData?.displayName || vendorId;

                            return (
                                <div key={vendorId} className="space-y-3">
                                    {/* Vendor Group Header */}
                                    <div className="flex items-center gap-1.5 border-b border-white/5 pb-1">
                                        <span className="text-sm">🏪</span>
                                        <h4 className="text-xs font-black uppercase tracking-widest" style={{ color: userThemeColor, ...stickerTextStyle(0.18) }}>
                                            {vendorName}
                                        </h4>
                                    </div>

                                    {/* Vendor Items */}
                                    <div className="space-y-3">
                                        {items.map(item => {
                                            const postImage = item.post.content.media?.[0]?.url || item.post.content.coverImageUrl;

                                            return (
                                                <div 
                                                    key={item.post.id} 
                                                    className="flex items-center gap-4 bg-black/30 border border-white/10 rounded-2xl p-3.5 hover:border-white/20 transition-all duration-300"
                                                >
                                                    {/* Item Thumbnail */}
                                                    {postImage ? (
                                                        <img 
                                                            src={postImage} 
                                                            alt="Product" 
                                                            className="w-16 h-16 rounded-xl object-cover border border-white/5 flex-shrink-0"
                                                        />
                                                    ) : (
                                                        <div className="w-16 h-16 rounded-xl bg-zinc-900 border border-white/5 flex items-center justify-center text-2xl flex-shrink-0">
                                                            📦
                                                        </div>
                                                    )}

                                                    {/* Item Details */}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-bold truncate">
                                                            {item.post.content.text || 'Storefront product'}
                                                        </p>
                                                        <p className="text-xs font-black text-green-400 mt-1" style={stickerTextStyle(0.15)}>
                                                            ${(item.post.price || 0).toFixed(2)}
                                                        </p>

                                                        {/* Quantity Controls */}
                                                        <div className="flex items-center gap-3 mt-2">
                                                            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-full px-2 py-1">
                                                                <button 
                                                                    onClick={() => onUpdateQuantity(item.post.id, -1)}
                                                                    className="p-1 hover:bg-white/10 rounded-full transition-colors cursor-pointer text-white/70 hover:text-white"
                                                                >
                                                                    <MinusIcon className="w-3.5 h-3.5" />
                                                                </button>
                                                                <span className="text-xs font-black w-4 text-center">
                                                                    {item.quantity}
                                                                </span>
                                                                <button 
                                                                    onClick={() => onUpdateQuantity(item.post.id, 1)}
                                                                    className="p-1 hover:bg-white/10 rounded-full transition-colors cursor-pointer text-white/70 hover:text-white"
                                                                >
                                                                    <PlusIcon className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Remove Button */}
                                                    <button 
                                                        onClick={() => onRemoveFromCart(item.post.id)}
                                                        className="p-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-full transition-colors cursor-pointer flex-shrink-0"
                                                        title="Remove from Cart"
                                                    >
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer Checkout Summary */}
                {cart.length > 0 && !isCheckingOut && (
                    <div className="p-6 border-t border-white/10 bg-zinc-950/60 backdrop-blur-md space-y-4">
                        <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between">
                                <span className="opacity-60">Subtotal</span>
                                <span className="font-bold">${subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="opacity-60">Est. Tax & Handling (8%)</span>
                                <span className="font-bold">${estTax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-base border-t border-white/5 pt-2">
                                <span className="font-black uppercase tracking-wider" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>Total</span>
                                <span className="font-black text-green-400" style={stickerTextStyle(0.15)}>${total.toFixed(2)}</span>
                            </div>
                        </div>

                        <button
                            onClick={handleCheckout}
                            className="w-full py-4 rounded-3xl bg-green-500 hover:bg-green-600 text-white font-black uppercase tracking-widest text-sm shadow-xl transition-all hover:scale-[1.02] active:scale-95 cursor-pointer flex items-center justify-center gap-2"
                            style={{ ...stickerTextStyle(0.18) }}
                        >
                            <span>🔒</span>
                            <span>Checkout Securely</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
